/**
 * DD Chat Message Component
 *
 * Displays individual chat messages with role-based styling.
 * Supports displaying sources from RAG responses.
 */

import React, { memo, useState, useCallback } from 'react';
import { View, Text } from 'react-native';
import Animated, { FadeInUp, FadeInDown } from 'react-native-reanimated';
import { Bot, User, FileText, History, ChevronDown, ChevronUp } from 'lucide-react-native';
import { cn } from '@/lib/cn';
import { formatMessageTime } from '@/lib/format';
import type { DDChatMessageProps, DDMessageMetadataExtended } from '@/lib/dd-chat/types';
import type { DDSource } from '@/lib/dd-chat/assistant.types';
import { SourceDetail } from '@/components/dd-chat/SourceCitation';
import AnimatedPressable from '@/components/AnimatedPressable';

function DDChatMessageComponent({
  message,
  isLast = false,
  isStreaming = false,
  onSuggestionTap,
}: DDChatMessageProps) {
  const isAssistant = message.role === 'assistant';
  const isUser = message.role === 'user';
  const [sourcesExpanded, setSourcesExpanded] = useState(false);
  const [selectedSource, setSelectedSource] = useState<DDSource | null>(null);

  // Cast metadata to extended type for RAG support
  const metadata = message.metadata as DDMessageMetadataExtended | undefined;
  const richSources = metadata?.richSources;
  const hasRichSources = richSources && richSources.length > 0;

  // Handle citation tap — set selected source for inline detail
  const handleCitationTap = useCallback(
    (index: number) => {
      if (!richSources) return;
      const source = richSources[index];
      if (!source) return;
      // Toggle off if tapping the same citation again
      setSelectedSource((prev) =>
        prev === source ? null : source
      );
    },
    [richSources]
  );

  const formattedTime = formatMessageTime(message.timestamp);

  return (
    <Animated.View
      entering={FadeInUp.duration(300)}
      className={cn(
        'mb-4',
        isUser && 'items-end',
        isAssistant && 'items-start'
      )}
    >
      {/* Message container */}
      <View
        className={cn(
          'max-w-[85%] rounded-2xl px-4 py-3',
          isUser && 'bg-blue-600 rounded-br-md',
          isAssistant && 'bg-zinc-800 rounded-bl-md'
        )}
      >
        {/* Header with role icon */}
        <View className="flex-row items-center mb-2">
          {isAssistant && (
            <View className="w-6 h-6 rounded-full bg-emerald-600/20 items-center justify-center mr-2">
              <Bot size={14} color="#10b981" />
            </View>
          )}
          {isUser && (
            <View className="w-6 h-6 rounded-full bg-blue-500/20 items-center justify-center mr-2">
              <User size={14} color="#3b82f6" />
            </View>
          )}
          <Text className="text-xs text-zinc-500">
            {isAssistant ? 'DD Assistant' : 'You'} · {formattedTime}
          </Text>
        </View>

        {/* Message content with inline citation support */}
        <MessageContent
          content={message.content}
          isStreaming={isStreaming && isLast}
          sources={isAssistant ? richSources : undefined}
          onCitationTap={isAssistant && hasRichSources ? handleCitationTap : undefined}
        />

        {/* Inline source detail card — shown when a citation marker is tapped */}
        {selectedSource && (
          <View className="mt-2">
            <SourceDetail
              source={selectedSource}
              onClose={() => setSelectedSource(null)}
            />
          </View>
        )}

        {/* Metadata (for assistant messages) */}
        {isAssistant && metadata && (
          <View className="mt-2 pt-2 border-t border-zinc-700/50">
            <View className="flex-row items-center gap-3">
              {metadata.processingTime && (
                <Text className="text-xs text-zinc-500">
                  {(metadata.processingTime / 1000).toFixed(1)}s
                </Text>
              )}
              {/* Legacy string sources */}
              {metadata.sources && metadata.sources.length > 0 && !hasRichSources && (
                <View className="flex-row gap-1 flex-wrap">
                  {metadata.sources.map((source) => (
                    <View
                      key={source}
                      className="px-2 py-0.5 bg-zinc-700/50 rounded"
                    >
                      <Text className="text-[10px] text-zinc-400 capitalize">
                        {source}
                      </Text>
                    </View>
                  ))}
                </View>
              )}
              {/* Rich sources indicator */}
              {hasRichSources && (
                <AnimatedPressable
                  onPress={() => setSourcesExpanded(!sourcesExpanded)}
                  className="flex-row items-center gap-1"
                >
                  <FileText size={12} color="#71717a" />
                  <Text className="text-xs text-zinc-400">
                    {richSources.length} source{richSources.length !== 1 ? 's' : ''}
                  </Text>
                  {sourcesExpanded ? (
                    <ChevronUp size={12} color="#71717a" />
                  ) : (
                    <ChevronDown size={12} color="#71717a" />
                  )}
                </AnimatedPressable>
              )}
            </View>
          </View>
        )}

        {/* Expanded sources section */}
        {isAssistant && hasRichSources && sourcesExpanded && (
          <SourcesSection sources={richSources} />
        )}
      </View>
    </Animated.View>
  );
}

/**
 * Message content with markdown-like formatting
 */
function MessageContent({
  content,
  isStreaming,
  sources,
  onCitationTap,
}: {
  content: string;
  isStreaming: boolean;
  sources?: DDSource[];
  onCitationTap?: (index: number) => void;
}) {
  // Split content into sections
  const sections = content.split('\n\n');

  return (
    <View>
      {sections.map((section, index) => (
        <View key={index} className={index > 0 ? 'mt-3' : ''}>
          {renderSection(section, sources, onCitationTap)}
        </View>
      ))}
      {isStreaming && (
        <View className="mt-2">
          <StreamingIndicator />
        </View>
      )}
    </View>
  );
}

/**
 * Render a section of content with basic markdown support
 */
function renderSection(
  section: string,
  sources?: DDSource[],
  onCitationTap?: (index: number) => void
) {
  const lines = section.split('\n');
  const elements: React.ReactNode[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Skip empty lines
    if (!trimmed) continue;

    // Headers (## )
    if (trimmed.startsWith('## ')) {
      elements.push(
        <Text key={i} className="text-base font-semibold text-white mb-2">
          {trimmed.replace('## ', '')}
        </Text>
      );
    }
    // Bold headers (**text**)
    else if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
      elements.push(
        <Text key={i} className="text-sm font-medium text-zinc-200 mt-2 mb-1">
          {trimmed.replace(/\*\*/g, '')}
        </Text>
      );
    }
    // Bullet points (- or •)
    else if (trimmed.startsWith('-') || trimmed.startsWith('•')) {
      const bulletText = trimmed.replace(/^[-•]\s*/, '');
      elements.push(
        <View key={i} className="flex-row mt-1">
          <Text className="text-zinc-400 mr-2">•</Text>
          <Text className="text-sm text-zinc-300 flex-1">
            {formatInlineText(bulletText, sources, onCitationTap)}
          </Text>
        </View>
      );
    }
    // Numbered lists
    else if (trimmed.match(/^\d+\./)) {
      const [num, ...rest] = trimmed.split('.');
      const listText = rest.join('.').trim();
      elements.push(
        <View key={i} className="flex-row mt-1">
          <Text className="text-zinc-400 mr-2 w-5">{num}.</Text>
          <Text className="text-sm text-zinc-300 flex-1">
            {formatInlineText(listText, sources, onCitationTap)}
          </Text>
        </View>
      );
    }
    // Regular paragraph
    else {
      elements.push(
        <Text key={i} className="text-sm text-zinc-300 leading-relaxed">
          {formatInlineText(trimmed, sources, onCitationTap)}
        </Text>
      );
    }
  }

  return <>{elements}</>;
}

/**
 * Format inline text with bold and citation marker support.
 *
 * Citation markers like [1], [2] are rendered as tappable blue superscript
 * badges when `sources` are provided and the marker index maps to a valid
 * source entry.
 */
function formatInlineText(
  text: string,
  sources?: DDSource[],
  onCitationTap?: (index: number) => void
): React.ReactNode {
  // Match **bold** text and [N] citation markers
  const parts = text.split(/(\*\*[^*]+\*\*|\[\d+\])/);

  if (parts.length === 1) {
    return text;
  }

  return parts.map((part, i) => {
    // Bold text
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <Text key={i} className="font-medium text-white">
          {part.replace(/\*\*/g, '')}
        </Text>
      );
    }

    // Citation marker [N]
    if (/^\[\d+\]$/.test(part)) {
      const num = parseInt(part.slice(1, -1), 10);
      const source = sources?.[num - 1]; // [1] maps to index 0
      if (!source) {
        // No matching source — render as plain text
        return <React.Fragment key={i}>{part}</React.Fragment>;
      }

      return (
        <Text
          key={i}
          onPress={() => onCitationTap?.(num - 1)}
          className="text-[10px] text-blue-400 font-semibold"
          style={{ lineHeight: 14 }}
          suppressHighlighting={false}
        >
          {' '}[{num}]
        </Text>
      );
    }

    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

/**
 * Streaming indicator animation
 */
function StreamingIndicator() {
  return (
    <View className="flex-row items-center gap-1">
      <View className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
      <View className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse delay-100" />
      <View className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse delay-200" />
    </View>
  );
}

/**
 * Sources section for RAG responses
 */
function SourcesSection({ sources }: { sources: DDSource[] }) {
  return (
    <Animated.View
      entering={FadeInDown.duration(200)}
      className="mt-3 pt-3 border-t border-zinc-700/30"
    >
      <Text className="text-xs font-medium text-zinc-400 mb-2">
        Sources Referenced
      </Text>
      <View className="gap-2">
        {sources.map((source, index) => (
          <SourceItem key={`${source.referenceId || index}-${index}`} source={source} />
        ))}
      </View>
    </Animated.View>
  );
}

/**
 * Individual source item
 */
function SourceItem({ source }: { source: DDSource }) {
  const isDocument = source.type === 'document';
  const isHistory = source.type === 'history';

  // Format relevance score as percentage
  const relevancePercent = source.relevance
    ? Math.round(source.relevance * 100)
    : null;

  // Determine relevance indicator color
  const getRelevanceColor = (score: number): string => {
    if (score >= 80) return 'bg-emerald-500';
    if (score >= 60) return 'bg-yellow-500';
    return 'bg-zinc-500';
  };

  return (
    <View className="bg-zinc-900/50 rounded-lg p-2.5">
      {/* Header row */}
      <View className="flex-row items-center gap-2 mb-1">
        {/* Source type icon */}
        <View
          className={cn(
            'w-5 h-5 rounded items-center justify-center',
            isDocument ? 'bg-blue-500/20' : 'bg-purple-500/20'
          )}
        >
          {isDocument ? (
            <FileText size={11} color="#3b82f6" />
          ) : (
            <History size={11} color="#a855f7" />
          )}
        </View>

        {/* Source label */}
        <Text
          className="text-xs font-medium text-zinc-300 flex-1"
          numberOfLines={1}
        >
          {source.label}
        </Text>

        {/* Relevance indicator */}
        {relevancePercent !== null && (
          <View className="flex-row items-center gap-1">
            <View
              className={cn(
                'w-1.5 h-1.5 rounded-full',
                getRelevanceColor(relevancePercent)
              )}
            />
            <Text className="text-[10px] text-zinc-500">{relevancePercent}%</Text>
          </View>
        )}
      </View>

      {/* Location (page number, section) */}
      {source.location && (
        <Text className="text-[10px] text-zinc-500 mb-1">{source.location}</Text>
      )}

      {/* Snippet preview */}
      {source.snippet && (
        <Text
          className="text-[11px] text-zinc-400 leading-relaxed"
          numberOfLines={2}
        >
          "{source.snippet}"
        </Text>
      )}
    </View>
  );
}

export const DDChatMessage = memo(DDChatMessageComponent);
